import { StoreMeasureSchema } from "./store-measures";
import { z } from "zod";
import type {
  KpiAutofillGap,
  KpiGapsSkipped,
  KpiMemberMisses,
} from "./kpi-gaps";
import {
  KPI_FORMULA_MAX_LENGTH,
  type KpiUnavailableReason,
} from "./kpi-formula";

/**
 * Explicit, numeric KPIs per team — the quantitative counterpart to Pulse.
 *
 * Pulse already carries a single qualitative `kpis` signal (red/yellow/green,
 * see `./pulse.ts`). That says whether a manager *feels* the numbers are on
 * track; it never captures the numbers themselves. These definitions + entries
 * add the actual values, on the exact same period grid, so trends and insights
 * can reason about movement rather than sentiment.
 *
 * Firestore layout:
 *   Businesses/{businessId}/kpiDefinitions/{kpiId}
 *   Businesses/{businessId}/kpiEntries/{periodStart}_{subjectId}
 *
 * Entries mirror `PulseScoreRecord` deliberately: one doc per (period,
 * subject), values held in a map keyed by definition id — the same shape as
 * `signals`. Period boundaries always come from the Pulse config helpers
 * (`getEffectivePulseConfig` / `getCheckInPeriodForConfig`), never from a
 * separate KPI schedule, so a team's KPIs and Pulse can never drift apart.
 */

// ---------------------------------------------------------------------------
// Definitions
// ---------------------------------------------------------------------------

/**
 * A team may track at most this many KPIs at once. A deliberate product
 * constraint, not a technical one: a handful of numbers people actually fill
 * in beat a dozen they ignore. Raised from three to six because a customer
 * needed to track more than three, then to fifteen when one needed more than
 * six. Enforced in the mutation layer (`upsertKpiDefinition`), because it is
 * a rule about the collection, not about a single document.
 */
export const MAX_ACTIVE_KPIS_PER_TEAM = 15;

/**
 * How many KPIs the AI suggests at once: a starting set, not a full team.
 * Kept below the team cap on purpose; fewer numbers get followed.
 */
export const MAX_SUGGESTED_KPIS = 6;

/** Which direction counts as improvement — needed to colour trends. */
export const KPI_DIRECTIONS = ["up", "down"] as const;
export type KpiDirection = (typeof KPI_DIRECTIONS)[number];

export const KpiDirectionSchema = z.enum(KPI_DIRECTIONS);

/**
 * Who a KPI's target is for (GRA-62). `per_member`: every seller should reach
 * it, and the team's target is the sum of its sellers' targets. `team`: the
 * team as a whole should reach it, and sellers have no target of their own
 * (unless their role sets one). A calculated KPI (a ratio) has one target at
 * both levels, so its scope is never consulted.
 */
export const KPI_TARGET_SCOPES = ["per_member", "team"] as const;
export type KpiTargetScope = (typeof KPI_TARGET_SCOPES)[number];
export const KpiTargetScopeSchema = z.enum(KPI_TARGET_SCOPES);

// Which toolkits are personal and which belong to the business now lives with
// the rest of the toolkit presentation, in `@graftus/composio`'s tool registry
// (`toolkitScope`). It was a two-slug list here, which meant every CRM was
// implicitly "read it through whichever team member happens to have connected
// it" — the shape that produced no KPI values at all in GRA-55.

export const KPI_SOURCE_ATTRIBUTIONS = ["member", "team"] as const;
export type KpiSourceAttribution = (typeof KPI_SOURCE_ATTRIBUTIONS)[number];

/**
 * Where a KPI's numbers come from. "manual" (the default, and the effective
 * state of every pre-existing definition) means humans type them into the
 * entry grid. "integration" means the nightly autofill job computes them from
 * a Composio-connected toolkit; `instruction` is a natural-language metric
 * spec the gather agent interprets — Composio execution is LLM-driven, so a
 * prose spec is the contract (e.g. "Antal HubSpot-affärer flyttade till
 * stängd-vunnen under perioden, räknat per deal owner"). Values are always
 * period totals on the Pulse grid.
 *
 * "calculated" ("Andra nyckeltal") holds no numbers at all: `expression` is
 * arithmetic over other KPIs' ids, evaluated at read time for whichever
 * period and subject is being asked about — see `./kpi-formula.ts`. Nothing is
 * written to `kpiEntries` for it, which is why a calculated result can never
 * be manually entered, autofilled, or left stale behind its sources.
 */
export const KpiSourceSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("manual") }),
  z.object({
    kind: z.literal("integration"),
    /** Uppercase Composio toolkit slug, e.g. "HUBSPOT". */
    toolkitSlug: z
      .string()
      .min(1)
      .max(60)
      .transform((s) => s.toUpperCase()),
    /** Natural-language metric spec the gather agent executes. */
    instruction: z.string().min(1).max(500),
    /**
     * "member": per-member values attributed by work-email match (plus a team
     * total when derivable). "team": only the team total is gathered.
     */
    attribution: z.enum(KPI_SOURCE_ATTRIBUTIONS).default("member"),
  }),
  z.object({
    kind: z.literal("calculated"),
    /**
     * Arithmetic over `{{kpiId}}` references, e.g.
     * `{{salesValueId}} / {{bookedMeetingsId}}`. Only shape is checked here;
     * `validateKpiFormula` owns the rules that need the whole definition set
     * (existence, scope, self-reference, cycles).
     */
    expression: z.string().min(1).max(KPI_FORMULA_MAX_LENGTH),
  }),
  z.object({
    /**
     * Store report measure (GRA-97). Holds no entries either: the value for a
     * team and period is aggregated at read time from the daily
     * `kpiMeasureValues` that report uploads write — see
     * `./store-measure-entries.ts`. Percentages are ratios of summed kronor.
     */
    kind: z.literal("upload"),
    measure: StoreMeasureSchema,
  }),
]);
export type KpiSource = z.infer<typeof KpiSourceSchema>;

/**
 * Whether a definition's values are recorded rather than derived. Calculated
 * KPIs come from formulas and upload KPIs from report measures; neither is
 * entered, autofilled or flagged as a gap.
 */
export function isRecordableKpi(def: Pick<KpiDefinition, "source">): boolean {
  return def.source.kind !== "calculated" && def.source.kind !== "upload";
}

/** KPIs whose value comes from store report measures (GRA-97). */
export function isUploadKpi(def: Pick<KpiDefinition, "source">): boolean {
  return def.source.kind === "upload";
}

export const MANUAL_KPI_SOURCE: KpiSource = { kind: "manual" };

/** Lenient read-side parse: any missing or malformed source means manual. */
export function parseKpiSource(raw: unknown): KpiSource {
  const parsed = KpiSourceSchema.safeParse(raw);
  return parsed.success ? parsed.data : MANUAL_KPI_SOURCE;
}

export interface KpiDefinition {
  id: string;
  /** Display name, e.g. "Bokade möten". */
  name: string;
  /** Free-text unit shown after the value, e.g. "SEK", "st", "%". */
  unit: string;
  direction: KpiDirection;
  /** Per-period target, or null when the KPI is tracked without a goal. */
  target: number | null;
  /**
   * Who `target` is for, see `KpiTargetScope`. Definitions written before
   * GRA-62 have none and read as `team`, which is how they were scored.
   */
  targetScope?: KpiTargetScope;
  /** Owning team. `null` = org-wide, applied to teams with no own definitions. */
  salesTeamId: string | null;
  /**
   * Name of the role KPI this corresponds to, when the per-person target should
   * come from the member's role rather than the flat team target. Lets an SDR
   * and an AE be measured on the same metric against different bars. Defaults
   * to matching on `name` when null, unless the KPI has its own per-seller
   * target — see `roleKpiFor`.
   */
  roleKpiName: string | null;
  /** Where the values come from — see `KpiSourceSchema`. */
  source: KpiSource;
  /**
   * How `target` is read (GRA-85). "absolute" (default): the number itself.
   * "vs_last_year": `changePct` against the same period last year, from the
   * store's reports, e.g. +3 for "at least last year + 3 %".
   */
  targetKind?: KpiTargetKind;
  /** For `vs_last_year`: the change against last year, in percent. */
  changePct?: number | null;
  /**
   * For absolute targets on summed measures: "per_day" scales the target by
   * the days with report data ("at most 10 000 kr svinn per day").
   */
  targetBasis?: KpiTargetBasis;
  createdAt: string | null;
  updatedAt: string | null;
  /** Set instead of deleting, so historical entries keep resolving their name. */
  archivedAt: string | null;
}

export function isActiveKpi(def: Pick<KpiDefinition, "archivedAt">): boolean {
  return def.archivedAt == null;
}

/** A role's per-month KPI target, from `BusinessRole.kpis`. */
export interface RoleKpiTarget {
  name: string;
  targetPerMonth: number;
}

/** Loosely-normalised month length; KPI targets are approximate by nature. */
const DAYS_PER_MONTH = 30;

function normalizeName(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * The role KPI that corresponds to a team KPI definition: an explicit
 * `roleKpiName` link when set, otherwise a name match. Case- and
 * whitespace-insensitive, since both names are free text a human typed.
 *
 * A KPI with its own per-seller target (`targetScope: "per_member"` plus a
 * `target`) never takes a role's target — not through a name match, and not
 * even through an explicit link (GRA-71). The link only applies to
 * team-scope KPIs, where it sets each seller's individual bar while the
 * team's own target stands untouched. Letting it reach a per-seller KPI
 * silently replaced the target the user just typed with one from a role,
 * possibly in another unit: "Försäljning" at 30 000 SEK per seller became a
 * team bar of 42 in dev, through an explicit link to "Avslut" (30/month).
 */
export function roleKpiFor(
  definition: Pick<KpiDefinition, "name" | "roleKpiName"> &
    Partial<Pick<KpiDefinition, "target" | "targetScope">>,
  roleKpis: RoleKpiTarget[],
): RoleKpiTarget | null {
  if (definition.targetScope === "per_member" && definition.target != null) {
    return null;
  }
  const linked = definition.roleKpiName?.trim();
  const wanted = normalizeName(linked || definition.name);
  return roleKpis.find((k) => normalizeName(k.name) === wanted) ?? null;
}

/**
 * The target a specific member should be measured against for a KPI this
 * period.
 *
 * A role KPI expresses a monthly expectation, so it is scaled to the Pulse
 * period length before comparison — a 40/month target on a 14-day period is
 * ~19. When the member's role carries no matching KPI, falls back to the flat
 * team target. Returns null when neither exists.
 */
export function resolveMemberKpiTarget(
  definition: Pick<
    KpiDefinition,
    "name" | "roleKpiName" | "target" | "targetScope" | "source"
  >,
  roleKpis: RoleKpiTarget[],
  periodDays: number | null,
): number | null {
  // Store report numbers belong to the department, never a person (GRA-97).
  if (definition.source.kind === "upload") return null;
  const roleKpi = periodDays != null ? roleKpiFor(definition, roleKpis) : null;
  if (roleKpi && periodDays != null) {
    return (roleKpi.targetPerMonth * periodDays) / DAYS_PER_MONTH;
  }
  // A team target is not every seller's bar (GRA-62); a ratio's target is.
  return effectiveTargetScope(definition) === "team" &&
    definition.source.kind !== "calculated"
    ? null
    : definition.target;
}

/** A definition's target scope, with pre-GRA-62 definitions read as `team`. */
export function effectiveTargetScope(
  definition: Partial<Pick<KpiDefinition, "targetScope">>,
): KpiTargetScope {
  return definition.targetScope ?? "team";
}

/**
 * The target a team is measured against for a KPI this period (GRA-62).
 *
 * `team` scope and calculated KPIs: the definition's target. `per_member`:
 * the sum of each member's own target (`resolveMemberKpiTarget`), so a team
 * of six at 10 each is measured against 60. Members without a target add
 * nothing; null when nobody has one.
 */
export function resolveTeamKpiTarget(
  definition: Pick<
    KpiDefinition,
    "name" | "roleKpiName" | "target" | "targetScope" | "source"
  >,
  members: { roleKpis: RoleKpiTarget[] }[],
  periodDays: number | null,
): number | null {
  if (
    definition.source.kind === "calculated" ||
    definition.source.kind === "upload" ||
    effectiveTargetScope(definition) === "team"
  ) {
    return definition.target;
  }
  let sum = 0;
  let any = false;
  for (const m of members) {
    const t = resolveMemberKpiTarget(definition, m.roleKpis, periodDays);
    if (t == null) continue;
    sum += t;
    any = true;
  }
  return any ? sum : null;
}

// ---------------------------------------------------------------------------
// Entries
// ---------------------------------------------------------------------------

export const KPI_SUBJECT_KINDS = ["member", "team"] as const;
export type KpiSubjectKind = (typeof KPI_SUBJECT_KINDS)[number];

/** Prefix that distinguishes a team-total subject id from a member id. */
export const KPI_TEAM_SUBJECT_PREFIX = "team:";

/**
 * Subject id for a KPI entry: a bare member id, or `team:{teamId}` for the
 * team total. Prefixing keeps both in one collection with one doc-id scheme,
 * and a member id can never collide with a team id.
 */
export function kpiSubjectId(kind: KpiSubjectKind, id: string): string {
  return kind === "team" ? `${KPI_TEAM_SUBJECT_PREFIX}${id}` : id;
}

export function parseKpiSubjectId(subjectId: string): {
  kind: KpiSubjectKind;
  id: string;
} {
  if (subjectId.startsWith(KPI_TEAM_SUBJECT_PREFIX)) {
    return {
      kind: "team",
      id: subjectId.slice(KPI_TEAM_SUBJECT_PREFIX.length),
    };
  }
  return { kind: "member", id: subjectId };
}

/** Doc id for a KPI entry — mirrors `pulseScores`' `{periodStart}_{memberId}`. */
export function kpiEntryDocId(periodStart: string, subjectId: string): string {
  return `${periodStart}_${subjectId}`;
}

/**
 * Who owns a recorded value: a human ("manual") or the nightly autofill job
 * ("auto"). Tracked per definition id because one entry doc holds values for
 * several KPIs — the doc-level `submittedBy` flips wholesale on every merge
 * write, so it cannot carry per-value provenance. An absent key means manual;
 * every value written before this field existed was typed in by a human.
 */
export const KPI_VALUE_SOURCES = ["manual", "auto"] as const;
export type KpiValueSource = (typeof KPI_VALUE_SOURCES)[number];

export const KpiValueSourcesMapSchema = z.record(
  z.string().min(1),
  z.enum(KPI_VALUE_SOURCES),
);

/** Marker uid the nightly autofill writes into `submittedBy`. */
export const KPI_AUTOFILL_SUBMITTER = "system:kpi-autofill";

export interface KpiEntryRecord {
  id: string;
  businessId: string;
  periodStart: string;
  periodEnd: string;
  teamId: string | null;
  subjectKind: KpiSubjectKind;
  subjectId: string;
  /** Set only when `subjectKind === "member"`. */
  memberId: string | null;
  /**
   * Definition id → value for the period. Recorded values as written, plus —
   * once the read path has resolved them — every calculated KPI that could be
   * worked out from them. `valueSources` covers only the recorded ones; a
   * calculated value is recognised by its definition's `source.kind`.
   */
  values: Record<string, number>;
  /** Definition id → who owns the value. Absent key = manual (legacy docs). */
  valueSources: Record<string, KpiValueSource>;
  /**
   * Definition id → why a calculated KPI has no value here. Derived at read
   * time and never stored, so it is absent on records built straight from a
   * document. Present and empty means "resolved, nothing missing"; an entry
   * for a divide-by-zero or an unfilled source lands here rather than in
   * `values`, because 0/Infinity/NaN would all read as answers.
   */
  unavailable?: Record<string, KpiUnavailableReason>;
  /**
   * Team entries only: CRM-read KPIs the nightly autofill could not produce
   * for this period, and whether the leader declined to fill them in
   * (GRA-59). See `openKpiGaps`.
   */
  autofillGaps?: Record<string, KpiAutofillGap>;
  gapsSkipped?: KpiGapsSkipped | null;
  /**
   * Team entries only: linked sellers the nightly autofill produced no value
   * for, per KPI, and why (GRA-102). See `KpiMemberMiss`.
   */
  autofillMemberMisses?: KpiMemberMisses;
  /**
   * Read-time only, on entries synthesised from store measures (GRA-97):
   * the same period last year per upload KPI, from the reports themselves.
   */
  lastYearValues?: Record<string, number>;
  /**
   * Read-time only, on entries synthesised from store measures: "org" is the
   * whole business (every store summed), "team" a store or a department.
   * Org-scope reads of upload KPIs take the "org" entry instead of summing
   * teams, which would count a store and its departments twice.
   */
  measureScope?: "org" | "team";
  /** Read-time only: days in the period that had report data. */
  measureDays?: Record<string, number>;
  submittedBy: string;
  submittedAt: string | null;
  updatedAt: string | null;
}

/**
 * Values are recorded as plain finite numbers. Negative is allowed (churn,
 * net-change KPIs); NaN/Infinity is not.
 */
export const KpiValuesMapSchema = z.record(
  z.string().min(1),
  z.number().finite(),
);

/**
 * The subset of `candidate` the autofill job may write for a subject: values
 * the entry doesn't have yet, and values the autofill itself wrote earlier
 * (each night recomputes the running period total). A human-entered value is
 * never overwritten — manual wins, always.
 */
export function autofillWritableValues(
  entry: Pick<KpiEntryRecord, "values" | "valueSources"> | null,
  candidate: Record<string, number>,
): Record<string, number> {
  if (entry == null) return { ...candidate };
  const out: Record<string, number> = {};
  for (const [kpiId, value] of Object.entries(candidate)) {
    const hasValue = typeof entry.values[kpiId] === "number";
    if (!hasValue || entry.valueSources[kpiId] === "auto") {
      out[kpiId] = value;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Derived helpers (pure — unit-tested)
// ---------------------------------------------------------------------------

/**
 * Relative KPI change below which a before/after comparison counts as noise.
 * Shared by action effect evaluation and experiment verdicts. Relative rather
 * than absolute because KPI units are arbitrary (SEK vs st vs %), unlike the
 * pulse scale's fixed 0..2 range.
 */
export const KPI_SHIFT_REL_THRESHOLD = 0.1;

/**
 * Whether `value` meets `target` given the KPI's direction. Returns null when
 * there is no target to judge against.
 */
export function meetsKpiTarget(
  def: Pick<KpiDefinition, "direction" | "target">,
  value: number,
): boolean | null {
  if (def.target == null) return null;
  return def.direction === "up" ? value >= def.target : value <= def.target;
}

/**
 * Signed change between two periods, normalised so positive always means
 * "moved the good way" regardless of direction.
 */
export function kpiImprovement(
  def: Pick<KpiDefinition, "direction">,
  previous: number,
  current: number,
): number {
  const delta = current - previous;
  return def.direction === "up" ? delta : -delta;
}

/**
 * The definitions that apply to a team: its own if it has any, otherwise the
 * org-wide set. Team definitions fully replace the org set rather than merging
 * — merging could push a team past `MAX_ACTIVE_KPIS_PER_TEAM`.
 */
export function resolveKpisForTeam(
  definitions: KpiDefinition[],
  salesTeamId: string | null,
  options?: {
    /**
     * Grocery stores (GRA-97): pass the team tree to inherit along
     * `parentTeamId`. A department gets the store's KPIs, and its own
     * definition for the same measure overrides the store's (e.g. Bageri's
     * own svinn target), nearest level first, capped at the focus limit.
     */
    inheritFromParents?: { id: string; parentTeamId: string | null }[];
  },
): KpiDefinition[] {
  const active = definitions.filter(isActiveKpi);
  if (options?.inheritFromParents && salesTeamId != null) {
    return resolveInheritedKpis(active, salesTeamId, options.inheritFromParents);
  }
  if (salesTeamId != null) {
    const own = active.filter((d) => d.salesTeamId === salesTeamId);
    if (own.length > 0) return own;
  }
  return active.filter((d) => d.salesTeamId == null);
}

/** What makes two definitions "the same KPI" across levels. */
function inheritanceKey(def: KpiDefinition): string {
  return def.source.kind === "upload"
    ? `measure:${def.source.measure}`
    : `name:${def.name.trim().toLowerCase()}`;
}

function resolveInheritedKpis(
  active: KpiDefinition[],
  salesTeamId: string,
  teams: { id: string; parentTeamId: string | null }[],
): KpiDefinition[] {
  const parentOf = new Map(teams.map((t) => [t.id, t.parentTeamId]));
  const chain: (string | null)[] = [];
  const seen = new Set<string>();
  let current: string | null = salesTeamId;
  while (current != null && !seen.has(current)) {
    seen.add(current);
    chain.push(current);
    current = parentOf.get(current) ?? null;
  }
  chain.push(null);

  const taken = new Set<string>();
  const out: KpiDefinition[] = [];
  for (const level of chain) {
    for (const def of active) {
      if ((def.salesTeamId ?? null) !== level) continue;
      const key = inheritanceKey(def);
      if (taken.has(key)) continue;
      taken.add(key);
      out.push(def);
    }
  }
  return out.slice(0, MAX_ACTIVE_KPIS_PER_TEAM);
}

// ---------------------------------------------------------------------------
// tRPC inputs
// ---------------------------------------------------------------------------

export const ListKpiDefinitionsInput = z.object({
  businessId: z.string().min(1),
  /** Omit for every definition; pass to resolve the set for one team. */
  salesTeamId: z.string().min(1).nullish(),
});
export type ListKpiDefinitionsInput = z.infer<typeof ListKpiDefinitionsInput>;

export const KPI_TARGET_KINDS = ["absolute", "vs_last_year"] as const;
export type KpiTargetKind = (typeof KPI_TARGET_KINDS)[number];
export const KPI_TARGET_BASES = ["period", "per_day"] as const;
export type KpiTargetBasis = (typeof KPI_TARGET_BASES)[number];

/**
 * The target a value is judged against for one period (GRA-85).
 *
 * - absolute, per period: `target`.
 * - absolute, per day: `target` × days with data (summed measures only; a
 *   percentage is the same bar whatever the length).
 * - vs last year: last year's value for the same period × (1 + changePct/100).
 *   For a percentage measure, last year's percentage is itself computed from
 *   last year's summed kronor.
 *
 * Null when the target can't be known (no last year, no days).
 */
export function resolvePeriodTarget(
  def: Pick<KpiDefinition, "target" | "targetKind" | "changePct" | "targetBasis" | "unit">,
  period: { lastYear?: number | null; days?: number | null },
): number | null {
  if ((def.targetKind ?? "absolute") === "vs_last_year") {
    if (period.lastYear == null || def.changePct == null) return null;
    return period.lastYear * (1 + def.changePct / 100);
  }
  if (def.target == null) return null;
  if (def.targetBasis === "per_day" && def.unit.trim() !== "%") {
    return period.days != null && period.days > 0 ? def.target * period.days : null;
  }
  return def.target;
}

/** Whether a definition's target changes from period to period. */
export function hasPeriodDependentTarget(
  def: Pick<KpiDefinition, "targetKind" | "targetBasis">,
): boolean {
  return def.targetKind === "vs_last_year" || def.targetBasis === "per_day";
}

export const UpsertKpiDefinitionInput = z.object({
  businessId: z.string().min(1),
  /** Omit to create. */
  kpiId: z.string().min(1).optional(),
  name: z.string().min(1).max(80),
  unit: z.string().max(20).default(""),
  direction: KpiDirectionSchema.default("up"),
  target: z.number().finite().nullish(),
  /** Who the target is for; absent keeps the stored scope (`team` when new). */
  targetScope: KpiTargetScopeSchema.nullish(),
  salesTeamId: z.string().min(1).nullish(),
  /** Link to a role KPI by name; null/absent = match on `name`. */
  roleKpiName: z.string().max(80).nullish(),
  /** Value source binding; null/absent = manual. */
  source: KpiSourceSchema.nullish(),
  /** GRA-85: absent keeps the stored kind ("absolute" when new). */
  targetKind: z.enum(KPI_TARGET_KINDS).nullish(),
  /** Change against last year in percent, e.g. 3 or -10. */
  changePct: z.number().finite().min(-100).max(1000).nullish(),
  targetBasis: z.enum(KPI_TARGET_BASES).nullish(),
});
export type UpsertKpiDefinitionInput = z.infer<typeof UpsertKpiDefinitionInput>;

export const ArchiveKpiDefinitionInput = z.object({
  businessId: z.string().min(1),
  kpiId: z.string().min(1),
});
export type ArchiveKpiDefinitionInput = z.infer<
  typeof ArchiveKpiDefinitionInput
>;

export const ListKpiEntriesInput = z.object({
  businessId: z.string().min(1),
  periodStart: z.string().min(1),
  periodEnd: z.string().min(1),
  salesTeamId: z.string().min(1).optional(),
});
export type ListKpiEntriesInput = z.infer<typeof ListKpiEntriesInput>;

export const UpsertKpiEntryInput = z.object({
  businessId: z.string().min(1),
  periodStart: z.string().min(1),
  periodEnd: z.string().min(1),
  subjectKind: z.enum(KPI_SUBJECT_KINDS),
  /** Member id, or team id when `subjectKind === "team"`. */
  subjectRefId: z.string().min(1),
  teamId: z.string().min(1).nullish(),
  values: KpiValuesMapSchema,
});
export type UpsertKpiEntryInput = z.infer<typeof UpsertKpiEntryInput>;

export const BulkUpsertKpiEntriesInput = z.object({
  businessId: z.string().min(1),
  periodStart: z.string().min(1),
  periodEnd: z.string().min(1),
  entries: z.array(
    z.object({
      subjectKind: z.enum(KPI_SUBJECT_KINDS),
      subjectRefId: z.string().min(1),
      teamId: z.string().min(1).nullish(),
      values: KpiValuesMapSchema,
    }),
  ),
});
export type BulkUpsertKpiEntriesInput = z.infer<
  typeof BulkUpsertKpiEntriesInput
>;

/**
 * History for the trend charts — newest periods first. Capped at 105 (just
 * over two years of weekly periods), not 26, so the Jämförelsedata
 * year-over-year chart and week grid (GRA-163) can reach back into last
 * year's matching weeks; `listKpiEntriesForPeriods` already chunks its
 * Firestore `in` query defensively for exactly this.
 */
export const ListKpiHistoryInput = z.object({
  businessId: z.string().min(1),
  salesTeamId: z.string().min(1).optional(),
  memberId: z.string().min(1).optional(),
  periodCount: z.number().int().min(1).max(105).default(6),
});
export type ListKpiHistoryInput = z.infer<typeof ListKpiHistoryInput>;

// ---------------------------------------------------------------------------
// AI-suggested definitions
// ---------------------------------------------------------------------------

export const SuggestKpiDefinitionsInput = z.object({
  businessId: z.string().min(1),
  salesTeamId: z.string().min(1).nullish(),
});
export type SuggestKpiDefinitionsInput = z.infer<
  typeof SuggestKpiDefinitionsInput
>;

/**
 * One AI-proposed KPI. Shaped so an accepted suggestion maps 1:1 onto
 * `UpsertKpiDefinitionInput` — the frontend never invents fields. `rationale`
 * is the model's one-sentence motivation shown on the suggestion card.
 */
export const SuggestedKpiDefinitionSchema = z.object({
  name: z.string().min(1).max(80),
  unit: z.string().max(20).default(""),
  direction: KpiDirectionSchema.default("up"),
  target: z.number().finite().nullable().default(null),
  targetScope: KpiTargetScopeSchema.default("per_member"),
  roleKpiName: z.string().max(80).nullable().default(null),
  source: KpiSourceSchema.default(MANUAL_KPI_SOURCE),
  rationale: z.string().max(300).default(""),
  /** Store templates (GRA-85/87) set these; model suggestions leave them out. */
  targetKind: z.enum(KPI_TARGET_KINDS).optional(),
  changePct: z.number().finite().nullable().optional(),
  targetBasis: z.enum(KPI_TARGET_BASES).optional(),
});
export type SuggestedKpiDefinition = z.infer<
  typeof SuggestedKpiDefinitionSchema
>;

export const SuggestKpiDefinitionsResult = z.object({
  suggestions: z
    .array(SuggestedKpiDefinitionSchema)
    .max(MAX_SUGGESTED_KPIS),
});
export type SuggestKpiDefinitionsResult = z.infer<
  typeof SuggestKpiDefinitionsResult
>;
